import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setToken, getToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Always attempt /auth/me — backend reads JWT from httpOnly cookie OR Bearer.
    // sessionStorage check is just an optimisation to skip when clearly logged out.
    if (!getToken() && !document.cookie.includes("auth_token")) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLoading(false);
      return data;
    } catch (err) {
      console.warn("Auth refresh failed:", err?.response?.status || err?.message);
      setToken(null);
      setUser(null);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const handlaggareLogin = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/handlaggare/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const bankid = useCallback(async () => {
    const { data } = await api.post("/auth/bankid");
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("Logout request failed:", err?.message); }
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, handlaggareLogin, register, bankid, logout, refresh }),
    [user, loading, login, register, bankid, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
